import equal from 'react-fast-compare';
import { IDataTableColumn, IDataTableColumnFilter, parseColumnWidth } from '@srm/ui/data-table';
import { mergeColumnOrder } from '@srm/ui/data-table/column-order';
import { IDataTableSavedState } from '@srm/ui/data-table/state';
import { IGridStateSettings } from '@models/response/IGridStateSettings';
import { isDataGridTemplateColumnId } from './dataGridSchema';
import { viewToTableState } from './dataGridViews';

const operations: Record<string, string> = {
    contains: 'содержит',
    notcontains: 'не содержит',
    startswith: 'начинается с',
    endswith: 'заканчивается на',
    '=': '=',
    '<>': '≠',
    '>': '>',
    '<': '<',
    '>=': '≥',
    '<=': '≤',
    between: 'от … до',
};

function normalizeFilter(filter: IDataTableColumnFilter | undefined) {
    const row = filter?.row;
    // виды старого DX-грида хранили значения воронки числами
    const header = filter?.header?.length
        ? filter.header
              .map((value) => (value === null ? null : String(value)))
              .sort((a, b) => String(a).localeCompare(String(b)))
        : undefined;
    return {
        row:
            row && row.value !== ''
                ? { op: row.op, value: Array.isArray(row.value) ? row.value.map(String) : String(row.value) }
                : undefined,
        header,
    };
}

function filterText(filter: IDataTableColumnFilter | undefined): string {
    const { row, header } = normalizeFilter(filter);
    const parts: string[] = [];
    if (row)
        parts.push(
            `${operations[row.op] ?? row.op} ${Array.isArray(row.value) ? row.value.join(' — ') : `«${row.value}»`}`,
        );
    if (header) parts.push(header.map((value) => (value === null ? '(пусто)' : `«${value}»`)).join(', '));
    return parts.join(' и ') || 'без фильтра';
}

/** Сравниваем только то, что сохранённый вид умеет восстановить.
 * Прокрутка, страница, выделение, закрепление и личная высота строк не входят в отличия. */
export function gridStateChanges<T>(
    view: IGridStateSettings | undefined,
    current: IDataTableSavedState,
    columns: readonly IDataTableColumn<T>[],
    defaultPageSize?: number,
): string[] {
    const saved = viewToTableState(view);
    if (!saved) return [];
    const changes: string[] = [];
    const fields = columns.map((column) => column.field);
    const known = new Set(fields);
    // Недостающие в виде колонки таблица ставит рядом с левым соседом по схеме,
    // а не в конец — сравниваем порядок, который она реально покажет.
    const order = (state: IDataTableSavedState) =>
        mergeColumnOrder(state.columnOrder ?? [], fields).filter((id) => !isDataGridTemplateColumnId(id));
    const filters = (state: IDataTableSavedState) =>
        new Map(state.columnFilters?.map((filter) => [filter.id, filter.value as IDataTableColumnFilter]));
    const previousFilters = filters(saved);
    const currentFilters = filters(current);
    const resized: string[] = [];

    for (const column of columns) {
        const id = column.field;
        if (isDataGridTemplateColumnId(id)) continue;
        const previous = previousFilters.get(id);
        const next = currentFilters.get(id);
        if (!equal(normalizeFilter(previous), normalizeFilter(next))) {
            changes.push(`${column.header}: ${filterText(previous)} → ${filterText(next)}`);
        }
        if ((saved.columnVisibility?.[id] !== false) !== (current.columnVisibility?.[id] !== false)) {
            changes.push(
                `${current.columnVisibility?.[id] === false ? 'Скрыта' : 'Показана'} колонка «${column.header}»`,
            );
        }
        const defaultWidth = parseColumnWidth(column.width);
        if ((saved.columnSizing?.[id] ?? defaultWidth) !== (current.columnSizing?.[id] ?? defaultWidth))
            resized.push(column.header);
    }
    if (resized.length) changes.push(`Ширина колонок: ${resized.join(', ')}`);
    if (!equal(order(saved), order(current))) changes.push('Изменён порядок колонок');
    const sorting = (state: IDataTableSavedState) => (state.sorting ?? []).filter((sort) => known.has(sort.id));
    if (!equal(sorting(saved), sorting(current))) changes.push('Изменена сортировка');
    const grouping = (state: IDataTableSavedState) => (state.grouping ?? []).filter((id) => known.has(id));
    if (!equal(grouping(saved), grouping(current))) changes.push('Изменена группировка');
    if ((saved.pageSize ?? defaultPageSize) !== (current.pageSize ?? defaultPageSize))
        changes.push('Изменено число строк на странице');
    return changes;
}
